import { predictWin, rate } from "openskill";

import type { Activity, Match, MatchResult, MultiplierConfig, Player, TeamResult } from "@/types";
import type { PlayerId } from "@/types/branded";
import { toMatchResultId, toPlayerId } from "@/types/branded";
import { generateUid } from "@/utils/index";
import { buildMultipliers, calculatePoints } from "../config/multipliers";

type ProcessMatchParams = {
  match: Match;
  players: Record<PlayerId, Player>;
  activity: Activity;
  multiplierConfig: MultiplierConfig;
  context?: {
    recentMatches?: Match[];
    playersById?: Record<PlayerId, Player>;
  };
};

export const processMatch = ({
  match,
  players,
  activity,
  multiplierConfig,
  context,
}: ProcessMatchParams): {
  updatedPlayers: Player[];
  result: MatchResult;
} => {
  const updatedPlayers: Record<PlayerId, Player> = {} as Record<PlayerId, Player>;
  match.teams.flat().forEach((pid) => {
    const p = players[pid];
    if (p && !updatedPlayers[pid])
      updatedPlayers[pid] = {
        ...p,
        rating: { ...p.rating },
        stats: { ...p.stats },
      };
  });

  const multipliers = buildMultipliers(multiplierConfig, activity.weight, {
    activityId: match.activityId,
    recentMatches: context?.recentMatches,
    currentMatch: match,
    playersById: context?.playersById,
  });

  const teamsRatings = match.teams.map((team) => team.map((pid) => players[pid]!.rating));
  const predictions = predictWin(teamsRatings);
  const newRatings = rate(teamsRatings, {
    rank: match.ranks,
    beta: activity.beta,
  });

  // First pass: compute raw points for every player
  const rawResults = match.teams.map((teamPlayerIds, teamIndex) => ({
    teamIndex,
    rank: match.ranks[teamIndex]!,
    players: teamPlayerIds.map((pid, playerIndex) => {
      const player = updatedPlayers[pid]!;
      const oldRating = { ...player.rating };
      const newRating = newRatings[teamIndex]![playerIndex]!;
      const deltaMu = newRating.mu - oldRating.mu;
      const expectedWin = predictions[teamIndex]!;

      const { points, breakdown } = calculatePoints({
        deltaMu,
        sigma: oldRating.sigma,
        expectedWin,
        player,
        multipliers,
      });

      return {
        pid,
        player,
        oldRating,
        newRating,
        deltaMu,
        expectedWin,
        points,
        breakdown,
      };
    }),
  }));

  // Find the minimum points across all players (last place baseline)
  const minPoints = Math.min(...rawResults.flatMap((t) => t.players.map((p) => p.points)));

  // Second pass: normalize so last place gets 0, apply to players
  const teamResults = rawResults.map((team) => ({
    teamIndex: team.teamIndex,
    rank: team.rank,
    players: team.players.map((r) => {
      const normalizedPoints = r.points - minPoints;

      r.player.rating = r.newRating;
      r.player.totalPoints += normalizedPoints;
      r.player.stats.gamesPlayed++;
      r.player.stats.winStreak = team.rank === 1 ? r.player.stats.winStreak + 1 : 0;

      return {
        playerId: toPlayerId(r.pid),
        name: r.player.name,
        ratingBefore: r.oldRating,
        ratingAfter: r.newRating,
        deltaMu: r.deltaMu,
        expectedWin: r.expectedWin,
        matchPoints: normalizedPoints,
        breakdown: r.breakdown,
      } satisfies TeamResult;
    }),
  }));

  const result: MatchResult = {
    id: toMatchResultId(generateUid()),
    matchId: match.id,
    activityId: match.activityId,
    teamResults,
  };

  return { updatedPlayers: Object.values(updatedPlayers), result };
};
